import React from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, Download, Clock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';

const TerminalStatusCard = ({ terminals, diagnoseTerminal, downloadAttendanceFromTerminal, showToast }) => {
  const { t } = useTranslation();

  return (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <Settings className="h-5 w-5" />
        {t('zktecoComp.terminalStatusTitle')}
      </CardTitle>
      <CardDescription>
        {t('zktecoComp.terminalStatusDesc')}
      </CardDescription>
    </CardHeader>
    <CardContent>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {terminals.map((terminal) => (
          <Card key={terminal.id}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">{terminal.name}</h3>
                  <p className="text-sm text-muted-foreground">SN: {terminal.serial_number}</p>
                </div>
                <Badge
                  variant={terminal.status === 'online' ? 'default' : 'secondary'}
                  className={terminal.status === 'online' ? 'bg-green-950/40 text-green-400' : 'bg-muted text-foreground'}
                >
                  {terminal.status === 'online' ? t('zktecoComp.online') : t('zktecoComp.offline')}
                </Badge>
              </div>
              <div className="text-sm grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-muted-foreground">
                <div className="col-span-2 font-medium text-foreground">
                  {terminal.model || terminal.terminal_type || '—'}
                </div>
                {terminal.firmware_version && (
                  <div className="col-span-2 font-mono text-xs">v{terminal.firmware_version}</div>
                )}
                <div>IP: {terminal.ip_address || '—'}</div>
                <div>{t('zktecoComp.mode')}: {terminal.communication_mode}</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="sm" variant="outline" onClick={() => diagnoseTerminal(terminal.id)} className="w-full whitespace-nowrap truncate">
                        {t('zktecoComp.diagnostic')}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('zktecoComp.readMemoryTooltip')}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="sm" variant="outline" onClick={() => downloadAttendanceFromTerminal(terminal.id)} className="w-full whitespace-nowrap truncate">
                        <Download className="h-4 w-4 mr-1" /> {t('zktecoComp.download')}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('zktecoComp.downloadPunchesTooltip')}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full whitespace-nowrap truncate"
                        onClick={() => showToast({ title: t('zktecoComp.comingSoonTitle'), description: t('zktecoComp.timeSyncSoon') })}
                      >
                        <Clock className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('zktecoComp.syncTime')}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {terminals.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Settings className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold mb-2">{t('zktecoComp.noTerminalTitle')}</h3>
          <p>{t('zktecoComp.noTerminalDesc')}</p>
        </div>
      )}
    </CardContent>
  </Card>
  );
};

export default TerminalStatusCard;
